import type { Route, Leg, Stop } from '@metro/routing'

/**
 * Branch-junction duplicate-stop collapse (fix b) — cross-platform mirror
 * of the iOS route-detail presentation dedup.
 *
 * At a branch junction two adjacent ride legs share the physical junction
 * station: ride leg N ends there, ride leg N+1 boards there. On the wire
 * these are DIFFERENT per-line node ids that resolve to the SAME physical
 * station (e.g. Camden Town Northern `northern.940GZZLUCTN-2` and
 * `northern.940GZZLUCTN-0`, both naptan `940GZZLUCTN`, joined by a transition).
 * Rendered verbatim the station appears twice in the stop list.
 *
 * This collapses ride leg N+1's LEADING stop when it is the SAME physical
 * station as ride leg N's terminus and the two rides are adjacent — one
 * train whose legs TfL split at the branch node. Physical identity is
 * compared naptan-first, falling back to `stationId` when a stop carries no
 * naptan — NOT the raw node id, so the CTN2/CTN0 pair collapses. A transfer
 * or walk leg between the rides is a real change of train (Kennington
 * Battersea → Bank branch, owner 2026-10-19; a night tram ending at
 * Centrale, TASK-986): the rider boards there, so the boarding stop stays.
 *
 * Guards:
 *   - A single-stop ride leg is never emptied — its lone stop is kept even
 *     when it physically equals the prior terminus (dropping it would lose
 *     the leg's only landing).
 *   - Returns the SAME route reference when nothing collapses (no
 *     allocation churn; callers may compare by identity).
 *
 * Applied at the presentation build so it covers BOTH online (TfL branch
 * split) and offline (schema graph) route sources.
 */
export function dedupeConsecutiveRideStops(route: Route): Route {
  let mutated = false
  let prevRideLastPhysical: string | null = null
  let prevRideLineId: string | null = null
  let changedSinceRide = false
  const newLegs: Leg[] = route.legs.map((leg) => {
    if (leg.type !== 'ride') {
      changedSinceRide = true
      return leg
    }
    const stops = leg.stops ?? []
    if (stops.length === 0) return leg

    let workingStops: readonly Stop[] = stops
    const leadPhysical = physicalId(stops[0])
    // Collapse the leading stop only when it matches the prior ride's
    // terminus, no change leg sits between the two rides, the leg has more
    // than one stop (never empty a leg), AND the prior ride is on the SAME
    // line: one train TfL split into two legs at a branch node → the shared
    // station shows once. A change leg (Kennington platform change) or a
    // DIFFERENT line is a real interchange → keep the boarding stop so the
    // transfer row + second line block render.
    if (
      prevRideLastPhysical !== null &&
      leadPhysical === prevRideLastPhysical &&
      !changedSinceRide &&
      leg.line_id === prevRideLineId &&
      stops.length > 1
    ) {
      workingStops = stops.slice(1)
      mutated = true
    }

    prevRideLastPhysical = physicalId(workingStops[workingStops.length - 1])
    prevRideLineId = leg.line_id ?? null
    changedSinceRide = false
    if (workingStops === stops) return leg
    return { ...leg, stops: workingStops }
  })

  if (!mutated) return route
  return { ...route, legs: newLegs }
}

/**
 * Physical station identity of a stop: naptan-first, `stationId` fallback.
 * Two per-line records of the same interchange (shared naptan, different
 * node id) collapse to one; a stop with no naptan keys on its node id.
 */
function physicalId(stop: Stop): string {
  return stop.naptanId ?? stop.stationId
}
